import { ScaleLinear, scaleLinear } from "d3-scale";
import { BaseType, select, Selection } from "d3-selection";

interface Dimensions {
	totalHeight: number;
	padding: { top: number; left: number; bottom: number; right: number };
	width: number;
	totalWidth: number;
	height: number;
}

type Axes = ScaleLinear<number, number>;
type Datum = { value: number, color: string };
type Chart = Selection<SVGGElement, unknown, null, unknown>;
type Tooltip = Selection<HTMLElement, unknown, null, unknown>;
type Segments = Selection<BaseType, Datum, SVGElement, unknown>;
type Percents = Selection<Element, unknown, SVGElement, Datum>;

export default class TestProgressChart {
	private readonly dimensions: Dimensions;
	private readonly colors: string[];

	constructor(colors: string[], width: number, height: number, padding: number) {
		width = width || 100; height = height || 20; padding = padding || 0;
		this.dimensions = this.getDimensions(width, height, padding, padding, padding, padding);
		this.colors = colors || ['#00000011', 'var(--dominant-color)'];
	}

	display(parent: Element, progress: number, total: number): void {
		const x = this.axis(total, this.dimensions);

		const chart = this.addChart(
			parent.querySelector('svg'),
			this.dimensions
		);
		const tooltip = this.addTooltip(parent);
		const bars = this.addBars(chart, [
			{ value: total, color: this.colors[0] },
			{ value: progress, color: this.colors[1] }
		]);

		this.fillBars(bars, x, this.dimensions.height);
		this.addPercent(chart, x, progress, this.dimensions.height);
		this.addEvents(bars, tooltip, parent.querySelector('svg'), progress, total);
	}

	getDimensions(width: number, height: number, padTop: number, padLeft: number, padBottom: number, padRight: number): Dimensions {
		return {
			width: width,
			height: height,
			padding: { top: padTop, left: padLeft, bottom: padBottom, right: padRight },
			totalWidth: width + padLeft + padRight,
			totalHeight: height + padTop + padBottom
		};
	}

	axis(total: number, dimensions: Dimensions): Axes {
		return scaleLinear()
			.domain([0, total])
			.range([0, dimensions.width]);
	}

	addChart(element: Element, dimensions: Dimensions): Chart {
		element.innerHTML = '';
		const svg = select(element)
			.attr('width', dimensions.totalWidth + '%')
			.attr('height', dimensions.totalHeight);
		return svg.append('g')
			.attr('transform', 'translate(' +
				dimensions.padding.left + ',' +
				dimensions.padding.top +
			')')
			.attr('width', dimensions.width + '%')
			.attr('height', dimensions.height);
	}

	addTooltip(parent: Element): Tooltip {
		return select(parent)
			.insert('div', ':first-child')
				.style('float', 'left')
				.style('width', 0)
			.insert('div', ':first-child')
				.attr('class', 'border rounded p-1 bg-light')
				.style('width', 'max-content')
				.style('display', 'none')
				.style('position', 'relative')
				.style('z-index', 10);
	}

	addBars(chart: Chart, data: Datum[]): Segments {
		return chart.selectAll('g')
			.data(data)
			.join('g')
				.attr('fill', d => d.color);
	}

	addEvents(bars: Segments, tooltip: Tooltip, element: SVGSVGElement, answered: number, total: number): Segments {
		return bars.on("mouseover", (event: MouseEvent, data: Datum) => {
			tooltip.style('display', 'block');
			select(event.currentTarget as Element)
				.select("rect")
				.style("opacity", 0.75);
		})
			.on("mouseleave", (event: MouseEvent, data: Datum) => {
				tooltip.style('display', 'none');
				select(event.currentTarget as Element)
					.select("rect")
					.style("opacity", 1);
			})
			.on("mousemove", (event: MouseEvent, data: Datum) => {
				const svgRect = element.getBoundingClientRect();
				tooltip.style('top', (
					event.pageY - svgRect.y - window.scrollY
				) + 'px')
					.style('left', (
						event.pageX - svgRect.x - window.scrollX + 20
					) + 'px')
					.html(`Progress: ${answered} / ${total}`);
			});
	}

	fillBars(bars: Segments, x: Axes, height: number): Segments {
		return bars.append("rect")
			.attr("x", d => x(0))
			.attr("y", 0)
			.attr("rx", 5)
			.attr("width", d => x(d.value) + '%')
			.attr("height", height);
	}

	addPercent(chart: Chart, x: Axes, answered: number, height: number): Percents {
		if (x(answered) >= 4) {
			return chart.append("text")
				.attr('x', x(answered) + '%')
				.attr('dx', -5)
				.attr('dy', '50%')
				.attr('dominant-baseline', 'central')
				.attr('text-anchor', 'end')
				.attr('fill', 'white')
				.text(Math.round(x(answered)) + '%');
		}
	}
}